// EXEMPT
import type { VolatileState } from "@/__main__/app-state.mjs";
import type { FeatureFlags } from "@/__main__/feature-flags.mjs";
import { GAME_SHORT_NAMES, THEME_DARK } from "@/app/constants.mjs";
import type { GameSymbol } from "@/app/games.mjs";
import type { AppSettings } from "@/data-models/app-settings.mjs";
import type { News } from "@/data-models/articles.mjs";
import type { CmsFeatureFlags } from "@/data-models/feature-flags.mjs";
import type { ValorantCoachingBenchmark } from "@/data-models/valorant-coaching-benchmark-data.mjs";
import type { User } from "@/feature-auth/models/user-model.mjs";
import type { SurveyState } from "@/feature-survey/actions.mjs";
import { BUILD_SORT } from "@/game-lol/constants/builds-constants.mjs";
import type { LolStaticData } from "@/game-lol/constants/constants.mjs";
import {
  FLASH_PLACEMENT_LEFT,
  FLASH_PLACEMENT_NULL,
  REGIONS_TO_SERVICES,
} from "@/game-lol/constants/constants.mjs";
import lolOverlaySettingsFixture from "@/game-lol/fixtures/lol-overlay-settings-fixture.json";
import type { ChampionMatchups } from "@/game-lol/models/lol-champion-matchups.mjs";
import type { ChampionsStats } from "@/game-lol/models/lol-champions-stats.mts";
import { LIVE_COMPARISON_OPTIONS } from "@/game-val/constants.mjs";
import type { ContentfulStateConfig } from "@/shared/contentful-constants.mjs";
import clone from "@/util/clone.mjs";
import gameShortNameReassign from "@/util/game-short-name-reassign.mjs";
// What belongs here: mainly empty objects which contain keys that are persisted.
// This guarantees that certain data structures exist and will not explode when trying
// to destructure.
const initialStateDefaults = {
  features: {} as FeatureFlags,
  lol: {
    patchesFromStatic: [],
    queues: [],
    staticData: {} as LolStaticData,
    profiles: {},
    profileRanks: {},
    lcuProfiles: {},
    localSummonerRanks: {},
    matches: {},
    localMatchlists: {},
    localPlayerBans: {},
    matchlists: {},
    arenaAugments: {},
    arenaAugmentsStats: {},
    championStats: {} as {
      [key: string | number]: { [key: string | number]: ChampionsStats };
    }, // singular champion
    championsStats: {} as { [key: string]: ChampionsStats }, // plural all champions
    championsArenaStats: [], // plural all champions
    championArenaStats: {}, // singular champion
    championsArenaDuosStats: [],
    championMatchups: {} as {
      [key: string | number]: { [key: string | number]: ChampionMatchups };
    }, // singular champion
    championBuilds: {},
    championDivStats: {},
    championStatsByMin: {},
    championStatsByDuration: {},
    championSynergies: {},
    championTips: {},
    championProMatches: {},
    currentPatch: {},
    matchups: {},
    live: {},
    overlay: {},
    championRoles: {},
    playerStyle: {},
    playerChampionStats: {},
    playerChampionSeasonStats: {},
    proBuildPros: {},
    proBuildTeams: {},
    proBuildMatchlist: [],
    proBuildTeamMatchlist: {},
    proBuildProMatchlist: {},
    proBuildChampionsReport: [],
    lpMatches: {},
    matchTimeline: {},
    lobbyMembers: [],
    doombotsLeaderboard: {},
  },
  val: {
    playerMatches: {},
    playerMatchWinHistory: {},
    rankedRatingsForAct: {},
    profiles: {},
    tips: {
      all: {},
    },
    content: {
      agents: [],
      ranks: [],
      acts: [],
      gameMaps: [],
    },
    cms: {
      agents: [],
      agentInsights: {},
      weapons: [],
      maps: [],
      entryInline: {},
    },
    match: {},
    matchWeaponStats: {},
    leaderboard: {},
    latestPlayerStats: {},
    playerAgentStats: {},
    playerMapStats: {},
    playerWeaponStats: {},
    playerActStats: {},
    actSummaries: {},
    live: {},
    coachingBenchmark: {},
    coachingData: {},
  },
  csgo: {
    profiles: {},
    matches: {},
    matchlists: {},
    playerStats: {},
    playerWeaponStats: {},
    live: {},
    liveGame: null,
    lastGameMode: null,
    stats: {
      weapons: {},
      maps: {},
    },
    playerMapStats: {},
  },
  cs2: {
    lineups: {},
    stats: { maps: {}, weapons: {}, pastMapStats: {}, pastWeaponStats: {} },
    leaderboard: {
      premier: {},
      competitive: [],
    },
  },
  apex: {
    profiles: {},
    matchlists: {},
    matches: {},
    meta: {
      seasons: {},
      weapons: {},
      legends: {},
    },
    playerStats: {},
    playerLegendStats: {},
    playerWeaponStats: {},
    live: null,
  },
  eft: {
    profiles: {},
    playerSeasonStat: {},
    playerWeaponStat: {},
    playerFactionStat: {},
    playerMapStat: {},
    matchlists: {},
    matchtiles: {},
    matches: {},
    weaponHits: {},
    weaponKills: {},
    leaderboards: {
      arena: {},
      maps: [],
      weapons: [],
      players: {},
    },
    liveGame: null,
    seasons: [],
    imgs: {
      weapons: {},
      maps: {},
    },
    items: {},
    itemsLocale: {},
    meta: {
      currentSeason: null,
      maps: {},
      seasons: {},
      items: {},
    },
    wiki: {
      calibers: {},
      categories: {},
      i18n: {},
      items: {},
      nodes: {},
      traders: {},
      version: [],
    },
  },
  tft: {
    playersById: {},
    patches: {},
    staticData: {},
    profiles: {},
    matches: {},
    matchlists: {},
    proMatchIds: [],
    matches_v2: {},
    matches_round: {},
    matchLists_v2: {},
    accounts_v2: {},
    stats: {
      filters: {},
      items: {},
      champions: {},
      traits: {},
      comps: {},
      income: {},
      augments: {},
      units: {},
      indiv_units: {},
      powerups: {},
      powerupTiers: {},
    },
    probuilds: {
      summoners: {},
      matches: {},
    },
    champions: {},
    summoners: {},
    items: {},
    classes: {},
    origins: {},
    augments: {},
    heroAugments: {},
    comps: {
      early: {},
      tierlist: {},
    },
    levelingMethods: {},
    overlaysInGame: {},
  },
  lor: {},
  fortnite: {
    profiles: {},
    profileSummaries: {},
    playerRanks: {},
    playerMatches: {},
    playerWeaponStats: {},
    matches: {},
    consoleMatches: {},
    matchlists: {},
    stats: {
      weapons: {},
      maps: {},
      ranks: {},
      weaponHistoricStats: {},
    },
    liveGame: null,
    seasons: [],
    leaderboard: {
      general: [],
      ranked: [],
      "power-ranking": [],
      teams: [],
    },
    rt: {},
    wiki: {
      i18n: {},
    },
    itemShopV2: {},
    reactions: {},
    userReactions: {},
    locker: {},
    items: {},
    active: {},
    vaulted: {},
    weaponItems: {},
  },
  deadlock: {
    heroes: {},
    abilitiesItems: {},
    abilitiesItemsLocal: {},
    heroesStats: [],
    heroItemsStats: {},
    profiles: {},
    profileStats: {},
    profileHeroStats: {},
    matches: {},
    matchlist: {},
    matchlistMeta: {},
    steam: {},
    liveGame: null,
  },
  rocket: {
    profiles: {},
    matches: {},
    liveGame: null,
  },
  marvelrivals: {
    tierlist: {},
    matchlist: {},
    matches: {},
    profiles: {},
    heroesStats: {},
  },
  minecraft: {
    wiki: {
      blocks: {},
      items: {},
    },
  },
  unknown: {
    matches: {},
    profiles: {},
    matchLists: {},
  },
  // TODO: this needs to be moved to feature-auth
  user: undefined as User,
  library: {
    recentlyAddedOverlays: [],
    lastInstalledGames: [],
  },
  search: {
    recents: [],
    favorites: [],
    regionData: {} as { "ISO 3166-1 alpha-2"?: string; remoteAddress?: string },
  },
  cms: {
    featureFlags: [] as CmsFeatureFlags,
  },
  marketing: {
    dismissed: [],
    jobs: [],
    departments: [],
    newNavItems: {},
    previousMatchTileAd: {},
    referrerInfo: {},
    seenPromoFeatures: {},
  },
  localState: {
    currentGame: undefined as GameSymbol,
    currentProfileId: makeHashByGames(null),
  },
  wiki: {} as GameHash<{
    GREEN?: string;
    BLUE?: string;
    GREEN_TIMESTAMP?: Date;
    BLUE_TIMESTAMP?: Date;
  }>,
  news: {
    preview: {},
    articles: {},
    latest: {
      staging: {},
      production: {},
    },
  } as News,
  contentful: {
    entry: {},
    entryInline: {},
    related: [],
    canonical: {},
  } as ContentfulStateConfig,
  settings: {
    onboarding: {},
    favoriteGames: [],
    riotRegion: REGIONS_TO_SERVICES.NA,
    lastLoggedInIdByGame: makeHashByGames<string>(null),
    loggedInAccounts: makeHashByGames(),
    selectedLanguage: null,
    theme: THEME_DARK,
    disableAnimations: false,
    dismissedNotifications: [],
    lol: {
      hasOnboardedBuilds: false,
      displayPopup: true,
      autoImportItems: true,
      autoImportRunes: true,
      autoImportSpells: false,
      buildSort: BUILD_SORT.common,
      prefersApexBuilds: false,
      minimizeLoadingScreen: false,
      tiltFreeMode: false,
      queuePopup: false,
      defaultFlashPlacement: FLASH_PLACEMENT_LEFT,
      manualFlashPlacement: FLASH_PLACEMENT_NULL,
      overlays: clone(lolOverlaySettingsFixture, { objectsOnly: true }),
      lastQueueId: null,
    },
    tft: {
      isSkinsEnabled: true,
      overlays: {},
    },
    lastPlayedGames: {},
    valorant: {
      muted: false,
      comparisonTier: "bronze" satisfies keyof ValorantCoachingBenchmark,
      liveMatchComparison: LIVE_COMPARISON_OPTIONS()[0].value,
      overlays: {
        isAgentSelectEnabled: true,
        isBenchmarkOverlayEnabled: true,
        isCombatOverlayEnabled: true,
        isLoadingScreenEnabled: true,
      },
    },
    survey: {
      isSurveyDisabled: false,
      linkedSurveyDismissedCount: 0,
    },
    lor: {},
    apex: {},
    cs2: {
      overlays: {
        isBenchmarkOverlayEnabled: true,
      },
    },
    eft: {
      overlays: {
        enableInventoryOverlay: true,
      },
    },
    fortnite: {},
    deadlock: {
      preferredMatchTab: "",
    },
    unknown: {
      toggle1: false,
      toggle2: true,
    },
    battles: {
      hideEventPromos: false,
    },
    wallet: {
      defaultPaymentMethodId: null,
    },
    minecraft: {
      overlays: {
        isBlockSightEnabled: true,
        isMiniMapEnabled: true,
        positions: {},
      },
    },
  } as AppSettings,
  debug: { expiration: "" },
  survey: {
    isSurveyDisabled: false,
    nextSurveySchedules: null,
    gamesUntilSurveyStart: null,
  } as SurveyState,
  // This is a holding area for all volatile state that shouldn't be persisted,
  // Although nothing is preventing devs from storing volatile state elsewhere,
  // it is advised to put this all in a single place so we can keep track better.
  // There is even an action, `setVolatileKV` to make this easier.
  volatile: {
    // Please keep this empty, nothing should be initialized in here.
  } as VolatileState,
};

export type GameHash<T = unknown> = Record<string, T>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function makeHashByGames(): GameHash<any>;
export function makeHashByGames<T extends object>(): GameHash<T>;
export function makeHashByGames<T>(initialValue: T): GameHash<T>;

export function makeHashByGames<T = unknown>(initialValue?: T): GameHash<T> {
  return Object.getOwnPropertySymbols(GAME_SHORT_NAMES).reduce(
    (hash, symbol) => {
      hash[gameShortNameReassign(GAME_SHORT_NAMES[symbol])] =
        initialValue !== undefined ? initialValue : {};
      return hash;
    },
    {},
  ) as GameHash<T>;
}

export default initialStateDefaults;
